import { useCallback } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import {
  useSavePriceOverridesMutation,
  useResetAllPriceOverridesMutation,
  useResetPriceOverrideMutation,
  useNotifyCustomerOfPriceOverridesMutation,
} from '@/redux/rtk';
import { useStaffPriceEditContext } from '@/mp/components/Cart/StaffPriceEditContext';
import { alertError } from '@/redux/actions/alert';
import {
  PriceOverrideInput,
  _2dMapCombinedQuote,
} from '@larki/shared/type/order.v2';
import Icon from '@/components/icon/Icon';

const RfqPriceEditBanner = ({
  jobId,
  quote,
  requestForQuoteId,
}: {
  jobId: number;
  quote: _2dMapCombinedQuote;
  requestForQuoteId?: number;
}) => {
  const dispatch = useDispatch();
  const {
    bannerState,
    overrides,
    pendingResets,
    cancelEditing,
    completedEditing,
    notifiedCustomer,
    dismissBanner,
    hasActiveOverrides,
  } = useStaffPriceEditContext();

  const [savePriceOverrides, { isLoading: isSaving }] =
    useSavePriceOverridesMutation();
  const [resetAllPriceOverrides, { isLoading: isResetting }] =
    useResetAllPriceOverridesMutation();
  const [resetPriceOverride, { isLoading: isResettingProduct }] =
    useResetPriceOverrideMutation();
  const [notifyCustomerOfPriceOverrides, { isLoading: isNotifying }] =
    useNotifyCustomerOfPriceOverridesMutation();

  const handleComplete = useCallback(async () => {
    if (overrides.size === 0 && pendingResets.size === 0) {
      completedEditing();
      return;
    }
    try {
      // Pending undos go before the save so the override save sees a clean baseline.
      if (pendingResets.size > 0) {
        await resetPriceOverride({
          jobId,
          data: {
            products: Array.from(pendingResets),
            requestForQuoteId,
          },
        }).unwrap();
      }
      if (overrides.size > 0) {
        const overrideInputs = Array.from(overrides, ([product, subtotal]) => ({
          product: product as PriceOverrideInput['product'],
          overrideSubtotal: subtotal,
        }));
        await savePriceOverrides({
          jobId,
          data: {
            overrides: overrideInputs,
            currentQuote: quote,
            requestForQuoteId,
          },
        }).unwrap();
      }
      completedEditing();
    } catch {
      dispatch(alertError('Failed to save price overrides'));
    }
  }, [
    jobId,
    quote,
    overrides,
    pendingResets,
    requestForQuoteId,
    savePriceOverrides,
    resetPriceOverride,
    completedEditing,
    dispatch,
  ]);

  const handleResetAll = useCallback(async () => {
    try {
      await resetAllPriceOverrides({
        jobId,
        data: { requestForQuoteId },
      }).unwrap();
      completedEditing();
    } catch {
      dispatch(alertError('Failed to reset prices'));
    }
  }, [
    jobId,
    requestForQuoteId,
    resetAllPriceOverrides,
    completedEditing,
    dispatch,
  ]);

  const handleNotifyCustomer = useCallback(async () => {
    try {
      await notifyCustomerOfPriceOverrides({
        jobId,
        data: { requestForQuoteId },
      }).unwrap();
      notifiedCustomer();
    } catch {
      dispatch(alertError('Failed to notify customer'));
    }
  }, [
    jobId,
    requestForQuoteId,
    notifyCustomerOfPriceOverrides,
    notifiedCustomer,
    dispatch,
  ]);

  if (bannerState === 'hidden') return null;
  const isEditing = bannerState === 'editing';

  return (
    <div
      style={{ zIndex: 1030 }}
      className={classNames(
        'staff-price-edit-banner',
        isEditing
          ? 'staff-price-edit-banner--editing'
          : 'staff-price-edit-banner--completed'
      )}
    >
      <div className='staff-price-edit-banner__alert position-relative'>
        <Icon
          icon={isEditing ? 'icon-warning' : 'circled-check'}
          hasIconClass={isEditing}
          className='mr-0'
        />
        <span className='font-14 font-weight--600'>
          {isEditing
            ? 'You are in Edit mode to change prices.'
            : 'Price changes are now visible by users.'}
        </span>
        <div className='button-group'>
          {isEditing ? (
            <>
              <button
                className='outline outline--action'
                onClick={handleComplete}
                disabled={
                  isSaving ||
                  isResettingProduct ||
                  (overrides.size === 0 && pendingResets.size === 0)
                }
              >
                {isSaving || isResettingProduct ? 'Saving...' : 'Complete'}
              </button>
              {hasActiveOverrides && (
                <button
                  className='outline'
                  onClick={handleResetAll}
                  disabled={isResetting}
                >
                  {isResetting ? 'Resetting...' : 'Reset to default'}
                </button>
              )}
              <button
                className='outline'
                onClick={cancelEditing}
                disabled={isSaving || isResettingProduct || isResetting}
              >
                Cancel
              </button>
            </>
          ) : bannerState === 'completed' && hasActiveOverrides ? (
            <button
              className='outline outline--action outline--success'
              onClick={handleNotifyCustomer}
              disabled={isNotifying}
            >
              {isNotifying ? 'Sending...' : 'Notify customer'}
            </button>
          ) : bannerState === 'notified' ? (
            <span className='staff-price-edit-banner__sent'>Sent!</span>
          ) : null}
        </div>
        {!isEditing && (
          <div className='dismiss-button-container mr-3'>
            <Button
              variant='light'
              className='icon-button bg-transparent'
              onClick={dismissBanner}
              aria-label='Close'
            >
              <Icon icon='close' className='mr-0' />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RfqPriceEditBanner;
